import { useCallback, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { BaseTooltipPlacement } from '@cbhq/cds-common/types';
import { IconButton } from '@cbhq/cds-web/buttons/IconButton';
import { SelectOption } from '@cbhq/cds-web/controls/SelectOption';
import { Dropdown } from '@cbhq/cds-web/dropdown/Dropdown';
import { Box } from '@cbhq/cds-web/layout/Box';
import { HStack } from '@cbhq/cds-web/layout/HStack';
import { VStack } from '@cbhq/cds-web/layout/VStack';
import { Avatar } from '@cbhq/cds-web/media/Avatar';
import { Tooltip } from '@cbhq/cds-web/overlays/tooltip/Tooltip';
import { TableCell, TableRow } from '@cbhq/cds-web/tables';
import { Tag } from '@cbhq/cds-web/tag/Tag';
import { Text } from '@cbhq/cds-web/typography/Text';
import { ActionType, ComponentType, logEvent, markStep } from '@cbhq/client-analytics';

import { CountOrSingleCell } from '../../../components/CountOrSingleCell';
import { useMinuteTick } from '../hooks/useMinuteTick';
import { useRoles } from '../hooks/useRoles';
import type { Column, UserCellMember } from '../types';
import { cancelPendingRoleMessages } from '../utils/cancelPendingRoleMessages';
import type { PendingRole } from '../utils/gracePeriod';
import { isPending as isRolePending } from '../utils/gracePeriodFormat';

import {
  getProductDisplayName,
  PRODUCTS,
  WORKSPACE_ROLE_PRODUCT_KEY,
} from './InviteTray/productCatalog';
import { ProductIcon } from './InviteTray/ProductIcon';
import type { PendingRoleInfoEntry } from './PendingRoleInfo';
import { PendingRoleInfo } from './PendingRoleInfo';

const WORKSPACE_ROLE_ADMIN = `${WORKSPACE_ROLE_PRODUCT_KEY}/legal_entity:admin`;
const WORKSPACE_ROLE_VIEWER = `${WORKSPACE_ROLE_PRODUCT_KEY}/legal_entity:viewer`;

const EMPTY_PENDING_ROLES: PendingRole[] = [];

const messages = defineMessages({
  you: {
    id: 'userCell.you',
    defaultMessage: 'You',
    description: 'Label shown in place of the email for the current user',
  },
  entities: {
    // TODO: Use ICU plural syntax ({count, plural, one {# entity} other {# entities}}) to
    // support locales with non-trivial plural rules (Russian, Arabic, Polish, etc.).
    id: 'userCell.entities',
    defaultMessage: '{count} entities',
    description: 'Tooltip trigger label showing how many entities a user belongs to',
  },
  cancelInvite: {
    id: 'userCell.cancelInvite',
    defaultMessage: 'Cancel invite',
    description: 'Dropdown option to cancel a pending invite for a user',
  },
  removeFromOrg: {
    id: 'userCell.removeFromOrg',
    defaultMessage: 'Remove from {orgName}',
    description: 'Dropdown option to remove a user from the organization',
  },
  moreActions: {
    id: 'userCell.moreActions',
    defaultMessage: 'More actions',
    description: 'Accessibility label for the actions dropdown button in a user row',
  },
  statusActive: {
    id: 'userCell.statusActive',
    defaultMessage: 'Active',
    description: 'Status label for an active team member',
  },
  statusInvitePending: {
    id: 'userCell.statusInvitePending',
    defaultMessage: 'Invite pending',
    description: 'Status label for a team member with a pending invite',
  },
  never: {
    id: 'userCell.never',
    defaultMessage: 'Never',
    description: 'Shown in the Last Online column when the user has never been online',
  },
  workspaceRoleAdmin: {
    id: 'userCell.workspaceRoleAdmin',
    defaultMessage: 'Admin',
    description: 'Workspace role column value for a member with Workspace admin access',
  },
  workspaceRoleViewer: {
    id: 'userCell.workspaceRoleViewer',
    defaultMessage: 'Viewer',
    description: 'Workspace role column value for a member with Workspace viewer access',
  },
});

const DROPDOWN_VALUES = ['remove-from-org', 'cancel-invite', 'cancel-role-change'] as const;
type DropdownValue = (typeof DROPDOWN_VALUES)[number];

function isDropdownValue(v: string): v is DropdownValue {
  return (DROPDOWN_VALUES as readonly string[]).includes(v);
}

const statusToColor = {
  active: 'green',
  pending: 'yellow',
} as const;

const statusToMessage = {
  active: messages.statusActive,
  pending: messages.statusInvitePending,
} as const;

const truncateStyle: React.CSSProperties = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const pressableInnerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  overflow: 'hidden',
  width: '100%',
};

const firstTableCellWhitespaceStyle = { paddingY: 0.5, paddingX: 0, paddingStart: 2 } as const;
const tableCellWhitespaceStyle = { paddingY: 0.5 } as const;
const lastTableCellWhitespaceStyle = { paddingY: 0.5, paddingEnd: 3, paddingStart: 2 } as const;

export type UserCellProps = {
  member: UserCellMember;
  orgName: string;
  columns: Column[];
  isRemoving?: boolean;
  onNameClick: (member: UserCellMember) => void;
  /** Opens a pending invite row. Pending rows aren't clickable without it. */
  onPendingInviteClick?: (member: UserCellMember) => void;
  onRemoveFromOrg: (member: UserCellMember) => void;
  onCancelInvite?: (member: UserCellMember) => void;
  showActions?: boolean;
  /** Whether the current user has permission to remove members from the org */
  canRemove?: boolean;
  /** Whether the current user has permission to edit member access */
  canEdit?: boolean;
  /** Whether the current user may cancel this member's pending invite */
  canCancelInvite?: boolean;
  /** Called from the "Revoke admin access" menu item for members with pending roles */
  onCancelPendingRole?: (member: UserCellMember) => void;
  /**
   * Whether the current user may cancel this member's pending roles. Evaluated on
   * each render, so the minute tick below re-checks it as pending roles take effect.
   */
  canCancelPendingRole?: (member: UserCellMember) => boolean;
};

export function UserCell({
  member,
  orgName,
  columns,
  isRemoving,
  onNameClick,
  onPendingInviteClick,
  onRemoveFromOrg,
  onCancelInvite,
  showActions = true,
  canRemove = false,
  canEdit = false,
  canCancelInvite = canEdit,
  onCancelPendingRole,
  canCancelPendingRole,
}: UserCellProps) {
  const { formatMessage, formatDate } = useIntl();
  const { roleMap, hasRoleDefinition } = useRoles();

  const isPending = member.status === 'pending';
  const isClickable = !isPending || onPendingInviteClick != null;
  let rowActionName: string | undefined;
  if (isClickable) {
    rowActionName = isPending ? 'team_pending_invite_row_open' : 'team_member_row_open';
  }

  const resolvedRoles = useMemo(
    () =>
      member.roles.filter(hasRoleDefinition).map((roleId) => ({
        label: roleMap[roleId].label,
        productKey: roleMap[roleId].productKey,
      })),
    [member.roles, roleMap, hasRoleDefinition],
  );

  const getProductLabel = useCallback(
    (productKey: string) => getProductDisplayName(productKey, formatMessage),
    [formatMessage],
  );

  // When the table has a separate Workspace role column (see Members.tsx),
  // account_management is excluded from Products.
  const hasWorkspaceRoleColumn = columns.includes('workspaceRole');

  // Iterate PRODUCTS rather than the member's role order so the icons render in
  // the same order as the member access tray: Coinbase Business, then Developer
  // Platform (see IDFE-3721).
  const productList = useMemo(() => {
    const memberProductKeys = new Set(resolvedRoles.map((role) => role.productKey));
    return PRODUCTS.filter(
      (product) =>
        memberProductKeys.has(product.productKey) &&
        (!hasWorkspaceRoleColumn || product.productKey !== WORKSPACE_ROLE_PRODUCT_KEY),
    ).map((product) => ({
      productKey: product.productKey,
      label: getProductLabel(product.productKey),
      iconName: product.iconName,
      iconBackground: product.iconBackground,
    }));
  }, [getProductLabel, hasWorkspaceRoleColumn, resolvedRoles]);

  const pendingRoles = member.pendingRoles ?? EMPTY_PENDING_ROLES;
  // `pendingRoles` is a fetch-time snapshot. Re-check against the clock (and
  // re-render every minute while any is pending) so "Revoke admin access"
  // disappears once every pending role has taken effect.
  const now = new Date();
  const hasLivePendingRoles = pendingRoles.some(({ effectiveAt }) =>
    isRolePending(effectiveAt, now),
  );
  useMinuteTick(hasLivePendingRoles);
  const entityCount = member.entities?.length ?? 0;

  // Pending Workspace admin grants. Shown as "Admin" with an info icon even
  // though `roles` also holds the compensating viewer during the grace period.
  const pendingAdminEntries = useMemo<PendingRoleInfoEntry[]>(() => {
    const pendingAdmins = pendingRoles.filter((pending) => pending.role === WORKSPACE_ROLE_ADMIN);
    // Name the entity whenever the member spans several, even if only one is pending.
    const labelEntityCount = Math.max(pendingAdmins.length, entityCount);
    return pendingAdmins.map((pending) => ({
      entityName: labelEntityCount > 1 ? pending.legalEntityName : undefined,
      effectiveAt: pending.effectiveAt,
    }));
  }, [entityCount, pendingRoles]);

  const pendingInviteAdminEntries = useMemo<PendingRoleInfoEntry[]>(
    () =>
      isPending &&
      member.pendingInviteEffectiveAt != null &&
      member.roles.includes(WORKSPACE_ROLE_ADMIN)
        ? [{ effectiveAt: member.pendingInviteEffectiveAt }]
        : [],
    [isPending, member.pendingInviteEffectiveAt, member.roles],
  );

  // Admin outranks Viewer when a member holds different Workspace roles across
  // entities. Only computed when the column actually renders. Matches the full
  // role ID, not just the product segment, so a future
  // account_management/<other_scope>:admin role isn't misclassified.
  const workspaceRole = useMemo<'admin' | 'viewer' | 'none'>(() => {
    if (!hasWorkspaceRoleColumn) return 'none';
    if (member.roles.includes(WORKSPACE_ROLE_ADMIN) || pendingAdminEntries.length > 0) {
      return 'admin';
    }
    if (member.roles.includes(WORKSPACE_ROLE_VIEWER)) return 'viewer';
    return 'none';
  }, [hasWorkspaceRoleColumn, member.roles, pendingAdminEntries.length]);

  const memberEntities = useMemo(() => member.entities ?? [], [member.entities]);

  const handleRowClick = useCallback(() => {
    if (!isPending) {
      onNameClick(member);
    } else {
      onPendingInviteClick?.(member);
    }
  }, [isPending, onNameClick, onPendingInviteClick, member]);
  const handleRowKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && isClickable) {
        e.preventDefault();
      }
    },
    [isClickable],
  );
  const handleActionCellClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);
  const handleActionCellKeyDown = useCallback((e: React.KeyboardEvent) => {
    e.stopPropagation();
  }, []);

  const rowStyle = useMemo(
    () => ({
      opacity: isRemoving ? 0 : 1,
      transition: 'opacity 150ms ease-in-out',
      cursor: isClickable ? 'pointer' : 'default',
    }),
    [isRemoving, isClickable],
  );

  // TODO: use relative time formatting, e.g. "Today", "3 days ago", "1 month ago", etc.
  const lastOnlineDisplay =
    member.lastOnline != null
      ? formatDate(member.lastOnline, { dateStyle: 'medium' })
      : formatMessage(messages.never);

  const removeFromOrgOption = canRemove ? (
    <SelectOption
      value={'remove-from-org' satisfies DropdownValue}
      title={
        <Text as="span" color="fgNegative">
          {formatMessage(messages.removeFromOrg, { orgName })}
        </Text>
      }
    />
  ) : null;

  const cancelRoleChangeOption =
    !isPending &&
    hasLivePendingRoles &&
    onCancelPendingRole != null &&
    canCancelPendingRole?.(member) ? (
      <SelectOption
        value={'cancel-role-change' satisfies DropdownValue}
        title={formatMessage(cancelPendingRoleMessages.revokeAdminAccess)}
      />
    ) : null;

  const hasActionOptions =
    member.status === 'pending'
      ? canCancelInvite && onCancelInvite != null
      : cancelRoleChangeOption != null || canRemove;

  let dropdownContent: React.ReactNode;
  if (member.status === 'pending') {
    dropdownContent = (
      <SelectOption
        testID="team-member-cancel-invite-option"
        value={'cancel-invite' satisfies DropdownValue}
        title={
          <Text as="span" color="fgNegative">
            {formatMessage(messages.cancelInvite)}
          </Text>
        }
      />
    );
  } else {
    dropdownContent = (
      <>
        {cancelRoleChangeOption}
        {removeFromOrgOption}
      </>
    );
  }

  const handleDropdownChange = useCallback(
    (value: string) => {
      if (!isDropdownValue(value)) return;
      if (value === 'cancel-invite') {
        logEvent('member_invite_cancelled_clicked', {
          componentType: ComponentType.dropdown,
          action: ActionType.select,
          entry_point: 'overflow_menu',
        });
        onCancelInvite?.(member);
      } else if (value === 'remove-from-org') {
        if (member.userUuid) {
          markStep('navigate_to_workspace_remove_member_modal');
        }
        onRemoveFromOrg(member);
      } else if (value === 'cancel-role-change') {
        onCancelPendingRole?.(member);
      }
    },
    [member, onCancelInvite, onCancelPendingRole, onRemoveFromOrg],
  );

  return (
    <TableRow
      onClick={isClickable ? handleRowClick : undefined}
      onKeyDown={isClickable ? handleRowKeyDown : undefined}
      style={rowStyle}
      data-testid="team-member-row"
      data-email={member.email}
      data-dd-action-name={rowActionName}
    >
      {columns.map((col) => {
        switch (col) {
          case 'name':
            return (
              <TableCell
                outerSpacing={firstTableCellWhitespaceStyle}
                key="name"
                overflow="truncate"
              >
                <HStack alignItems="center" gap={2} style={pressableInnerStyle}>
                  <Avatar
                    size="l"
                    colorScheme={member.avatarColor}
                    name={member.name}
                    src={member.avatarSrc}
                    alt={member.name}
                    shape="circle"
                  />
                  <VStack minWidth={0}>
                    <Text font="label1" style={truncateStyle}>
                      {member.name}
                    </Text>
                    <Text font="label2" color="fgMuted" style={truncateStyle}>
                      {member.isCurrentUser ? formatMessage(messages.you) : member.email}
                    </Text>
                  </VStack>
                </HStack>
              </TableCell>
            );
          case 'entity':
            return (
              <TableCell outerSpacing={tableCellWhitespaceStyle} key="entity" overflow="truncate">
                <Box width="100%" minWidth={0}>
                  {memberEntities.length === 1 ? (
                    <Text as="span" font="label2" style={truncateStyle}>
                      {memberEntities[0]}
                    </Text>
                  ) : (
                    <CountOrSingleCell items={memberEntities} countMessage={messages.entities} />
                  )}
                </Box>
              </TableCell>
            );
          case 'product':
            if (productList.length === 0) {
              return (
                <TableCell
                  outerSpacing={tableCellWhitespaceStyle}
                  key="product"
                  overflow="truncate"
                >
                  {/* Dash for missing product access, consistent with missing Workspace role (see IDFE-3722) */}
                  <Text as="span" font="label2" color="fgMuted" overflow="truncate">
                    —
                  </Text>
                </TableCell>
              );
            }

            return (
              <TableCell outerSpacing={tableCellWhitespaceStyle} key="product">
                <HStack alignItems="center" gap={1}>
                  {productList.map((product) => (
                    <Tooltip
                      key={product.productKey}
                      content={product.label}
                      placement={'bottom-start' as BaseTooltipPlacement}
                    >
                      <HStack as="span" accessibilityLabel={product.label}>
                        <ProductIcon name={product.iconName} background={product.iconBackground} />
                      </HStack>
                    </Tooltip>
                  ))}
                </HStack>
              </TableCell>
            );
          case 'workspaceRole':
            return (
              <TableCell
                outerSpacing={tableCellWhitespaceStyle}
                key="workspaceRole"
                overflow="truncate"
              >
                {workspaceRole === 'none' ? (
                  // Dash for a missing role, consistent with missing product access
                  // (see IDFE-3722).
                  <Text as="span" font="label2" color="fgMuted" overflow="truncate">
                    —
                  </Text>
                ) : (
                  // Admin reads in the primary color, Viewer stays muted, so the two
                  // roles are visually distinct (see IDFE-3720).
                  <HStack alignItems="center" gap={0.25} minWidth={0}>
                    <Text
                      as="span"
                      font="label2"
                      color={workspaceRole === 'admin' ? 'fg' : 'fgMuted'}
                      overflow="truncate"
                    >
                      {formatMessage(
                        workspaceRole === 'admin'
                          ? messages.workspaceRoleAdmin
                          : messages.workspaceRoleViewer,
                      )}
                    </Text>
                    {workspaceRole === 'admin' && pendingAdminEntries.length > 0 ? (
                      <PendingRoleInfo
                        variant="pendingRole"
                        roleName={formatMessage(messages.workspaceRoleAdmin)}
                        entries={pendingAdminEntries}
                      />
                    ) : null}
                    {workspaceRole === 'admin' && pendingInviteAdminEntries.length > 0 ? (
                      <PendingRoleInfo
                        variant="pendingInvite"
                        roleName={formatMessage(messages.workspaceRoleAdmin)}
                        entries={pendingInviteAdminEntries}
                      />
                    ) : null}
                  </HStack>
                )}
              </TableCell>
            );
          case 'status':
            return (
              <TableCell outerSpacing={tableCellWhitespaceStyle} key="status" overflow="truncate">
                <Tag colorScheme={statusToColor[member.status]}>
                  {formatMessage(statusToMessage[member.status])}
                </Tag>
              </TableCell>
            );
          case 'lastOnline':
            return (
              <TableCell
                outerSpacing={tableCellWhitespaceStyle}
                key="lastOnline"
                overflow="truncate"
              >
                <Text as="p" display="block" font="label2" color="fgMuted" overflow="truncate">
                  {lastOnlineDisplay}
                </Text>
              </TableCell>
            );
          default:
            return col satisfies never;
        }
      })}

      {showActions && (
        <TableCell outerSpacing={lastTableCellWhitespaceStyle} width={48} justifyContent="center">
          {!member.isCurrentUser && hasActionOptions && (
            <Box onClick={handleActionCellClick} onKeyDown={handleActionCellKeyDown}>
              <Dropdown content={dropdownContent} onChange={handleDropdownChange}>
                <IconButton
                  name="more"
                  variant="secondary"
                  transparent
                  accessibilityLabel={formatMessage(messages.moreActions)}
                  data-dd-action-name="team_member_overflow_open"
                  data-testid="team-member-actions-button"
                />
              </Dropdown>
            </Box>
          )}
        </TableCell>
      )}
    </TableRow>
  );
}
